import React, { useState, useEffect } from 'react'

import { Row } from '@shadcn/components/ui/row';
import { Col } from '@shadcn/components/ui/col';
import { Card, CardContent as CardBody} from '@shadcn/components/ui/card'

import Breadcrumbs from '../../components/Common/Breadcrumb';
import "./datatables.scss";

import useQuery from '../../helpers/JIFI/useQuery';

import { useHistory, useLocation } from "react-router-dom";
import { toast, ToastContainer } from 'react-toastify';
import { Button } from '@shadcn/components/ui/button';
import { Download, Loader2 } from 'lucide-react';


import Select from '@shadcn/components/ui/react-select';

import * as moment from 'moment-timezone';



import { TablePagination } from '@components/supply-chain/TablePagination';
import { TableSkeleton } from '@shadcn/components/ui/table';
import { stockMovementSourceLabel } from './constants';
import {
  getInformationalMovementItems,
  getMovementSummaryDeltas,
  groupMovementHistoryRows,
  isInformationalMovement,
} from './movement-history.utils';
import {
  MOVEMENT_EXPORT_DEFAULT_RANGE_DAYS,
  buildMovementExportFilters,
  csvHasRows,
  downloadBlob,
  exportMovementHistoryCsv,
  hasMovementDateRange,
  movementTypeToReason,
} from './movement-history-export';



const MOVEMENT_SKU_PREVIEW_LIMIT = 20;
const MOVEMENT_FILTER_BORDER_COLOR = '#ced4da';
const movementFilterInputStyle = {
  height: 38,
  borderColor: MOVEMENT_FILTER_BORDER_COLOR,
};
const movementFilterSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: 38,
    borderColor: MOVEMENT_FILTER_BORDER_COLOR,
    boxShadow: state.isFocused
      ? `0 0 0 1px ${MOVEMENT_FILTER_BORDER_COLOR}`
      : 'none',
    '&:hover': {
      borderColor: MOVEMENT_FILTER_BORDER_COLOR,
    },
  }),
  indicatorSeparator: provided => ({
    ...provided,
    backgroundColor: MOVEMENT_FILTER_BORDER_COLOR,
  }),
};

const MovementSkuList = ({ items, type, locationCode, truncate }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const shouldTruncate = truncate && !isExpanded;
  const visibleItems = shouldTruncate ? items.slice(0, MOVEMENT_SKU_PREVIEW_LIMIT) : items;
  const hiddenCount = truncate ? items.length - MOVEMENT_SKU_PREVIEW_LIMIT : 0;
  const canToggle = truncate && items.length > MOVEMENT_SKU_PREVIEW_LIMIT;
  const quantityClass = type === 'in'
    ? 'text-success text-emerald-600 font-semibold'
    : type === 'out'
      ? 'text-danger text-red-500 font-semibold'
      : 'text-muted text-slate-500 font-semibold';
  const locationQuery = locationCode
    ? `?locationCode=${encodeURIComponent(locationCode)}`
    : '';

  return (
    <div>
      {visibleItems.map(val => (
        <a
          key={`${val.sku}-${val.field || 'onHand'}`}
          className="d-block cursor-pointer hover:underline text-inherit"
          href={`/stock-movements/sku/${encodeURIComponent(val.sku)}${locationQuery}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {val.sku}{val.field && val.field !== 'onHand' ? ` · ${val.label}` : ''}
          {" "} x <span className={quantityClass}>{type === 'in' ? `+${val.count}` : val.count}</span>
        </a>
      ))}
      {canToggle && (
        <button
          type="button"
          className="btn btn-link p-0 mt-1 font-semibold text-slate-500"
          onClick={() => setIsExpanded(current => !current)}
        >
          {isExpanded ? 'show less' : `... ${hiddenCount} more`}
        </button>
      )}
    </div>
  );
};


const MovementPage = () => {
  const history = useHistory();
  const location = useLocation();
  const [refresh, setRefresh] = useState(true)
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [pageCursors, setPageCursors] = useState({ 1: undefined });
  const [loading, setlLoading] = useState(false)
  const [selectedType, setSelectedType] = useState(null)
  const [selectedLocation, setSelectedLocation] = useState(null)
  const [selectedSource, setSelectedSource] = useState(null)
  const [skuInput, setSkuInput] = useState('')
  const [skuFilter, setSkuFilter] = useState('')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [exporting, setExporting] = useState(false)

  
  

  const [locationOptions, locationState] = useQuery({
    url: `/inventory/movement-history/locations`,
    refresh: true
  })

  const movementFilters = {
    movementType: selectedType?.value,
    locationCode: selectedLocation?.code,
    locationName: !selectedLocation?.code
      ? selectedLocation?.value
      : undefined,
    source: selectedSource?.value,
    sku: skuFilter.trim() || undefined,
    fromDate: startDate || undefined,
    toDate: endDate || undefined,
  }

  const [data, state] = useQuery({
    url: `/inventory/movement-history/`,
    refresh,
    queryData: { 
      ...movementFilters,
      page: page, 
      perPage: perPage, 
      cursorPagination: true,
      cursor: pageCursors[page]
    }
  });
  const historyData = data && data.data
  const sortedHistoryData = groupMovementHistoryRows(historyData || [])
  const count = data ? data.count : 0

  useEffect(() => {
    document.title = `Movement History | JIFI`
  }, [])

  useEffect(() => {
    const timeout = setTimeout(() => {
      setSkuFilter(skuInput.trim())
      setPage(1)
      setPageCursors({ 1: undefined })
    }, 350)

    return () => clearTimeout(timeout)
  }, [skuInput])

  useEffect(() => {
    if (!data) return
    setPageCursors(current => {
      const next = { ...current }
      if (data.nextCursor) {
        next[page + 1] = data.nextCursor
      } else {
        delete next[page + 1]
      }
      return next
    })
  }, [data, page])

  useEffect(() => {
    if (location.pathname === '/movement-history') {
      history.replace('/movement-history-enhanced')
    }
  }, [location.pathname, history])

  const [orders, setOrders] = useState([])

  const handleSelectType = selectedType => {
    setSelectedType(selectedType)
    setPage(1)
    setPageCursors({ 1: undefined })
  }

  const handleSelectLocation = selectedLocation => {
    setSelectedLocation(selectedLocation)
    setPage(1)
    setPageCursors({ 1: undefined })
  }

  const handleSelectSource = selectedSource => {
    setSelectedSource(selectedSource)
    setPage(1)
    setPageCursors({ 1: undefined })
  }

  const handleSkuChange = event => {
    setSkuInput(event.target.value)
  }

  const handleStartDateChange = (event) => {
    setStartDate(event.target.value)
    setPage(1)
    setPageCursors({ 1: undefined })
  }

  const handleEndDateChange = (event) => {
    setEndDate(event.target.value)
    setPage(1)
    setPageCursors({ 1: undefined })
  }

  const handleExport = async () => {
    setExporting(true)
    try {
      // The backend builds the file (one row per table row) and streams it in one request.
      const csv = await exportMovementHistoryCsv(buildMovementExportFilters(movementFilters))
      if (!(await csvHasRows(csv))) {
        // The 30-day default is not in the date inputs, so name it rather than blame the filters.
        toast.info(
          hasMovementDateRange(movementFilters)
            ? 'No movements match these filters.'
            : `No movements in the last ${MOVEMENT_EXPORT_DEFAULT_RANGE_DAYS} days. Set a FROM/TO DATE to export older movements.`
        )
        return
      }
      downloadBlob(csv, `movement-history-${moment.tz('Australia/Melbourne').format('YYYY-MM-DD-HHmm')}.csv`)
    } catch (error) {
      console.log(error)
      toast.error('Could not export movement history. Please try again.')
    } finally {
      setExporting(false)
    }
  }

  const movementTypeOptions = [
    { label: 'All', value: undefined },
    { label: movementTypeToReason.CYCLE_COUNT, value: 'CYCLE_COUNT' },
    { label: movementTypeToReason.SHIPMENT, value: 'SHIPMENT' },
    { label: movementTypeToReason.TRANSFER, value: 'TRANSFER' },
  ]

  const sourceOptions = [
    { label: 'All Sources', value: undefined },
    ...Object.entries(stockMovementSourceLabel).map(([value, label]) => ({
      label,
      value,
    })),
  ]

  return <div className="page-content">
    <div className="w-full px-3 mx-auto xl:max-w-[85%]">
      <ToastContainer />
      <Breadcrumbs title="Inventory" breadcrumbItem={'Movement History'} />
      <div className="mb-3 flex justify-end">
        <Button
          variant="outline"
          size="sm"
          className="gap-2"
          onClick={handleExport}
          disabled={exporting || state !== 'success'}
        >
          {exporting
            ? <Loader2 className="w-4 h-4 animate-spin" />
            : <Download className="w-4 h-4" />}
          {exporting ? 'Exporting…' : 'Export CSV'}
        </Button>
      </div>
      <p className="mb-3 text-sm text-muted-foreground text-right">
        Exports current filters (last {MOVEMENT_EXPORT_DEFAULT_RANGE_DAYS} days if none set)
      </p>
      <Row className="mb-4 gap-3 flex-wrap xl:flex-nowrap">
        <Col className="w-full sm:w-[30%] xl:w-auto xl:flex-1 xl:min-w-0">
          <div className="select2-container mb-4">
            <label className="control-label">MOVEMENT TYPE</label>
            <Select
              placeholder="All"
              title="Movement Type"
              value={selectedType}
              onChange={handleSelectType}
              options={movementTypeOptions}
              styles={movementFilterSelectStyles}
            />
          </div>
        </Col>
        <Col className="w-full sm:w-[30%] xl:w-auto xl:flex-1 xl:min-w-0">
              <div className="select2-container mb-4">
                <label className="control-label">LOCATION</label>
                <Select
                  placeholder="All Locations"
                  value={selectedLocation}
                  onChange={handleSelectLocation}
                  options={locationOptions || []}
                  styles={movementFilterSelectStyles}
                />
              </div>
            </Col>
            <Col className="w-full sm:w-[30%] xl:w-auto xl:flex-1 xl:min-w-0">
              <div className="mb-4">
                <label className="control-label" htmlFor="movement-history-sku">SKU</label>
                <input
                  id="movement-history-sku"
                  type="text"
                  className="form-control"
                  style={movementFilterInputStyle}
                  placeholder="Search SKU"
                  value={skuInput}
                  onChange={handleSkuChange}
                />
              </div>
            </Col>
            <Col className="w-full sm:w-[30%] xl:w-auto xl:flex-1 xl:min-w-0">
              <div className="select2-container mb-4">
                <label className="control-label">SOURCE</label>
                <Select
                  placeholder="All Sources"
                  value={selectedSource}
                  onChange={handleSelectSource}
                  options={sourceOptions}
                  styles={movementFilterSelectStyles}
                />
              </div>
            </Col>
            <Col className="w-full sm:w-[30%] xl:w-auto xl:flex-1 xl:min-w-0">
              <div className="mb-4">
                <label className="control-label">FROM DATE</label>
                <input
                  type="date"
                  className="form-control"
                  style={movementFilterInputStyle}
                  value={startDate}
                  onChange={handleStartDateChange}
                />
              </div>
            </Col>
            <Col className="w-full sm:w-[30%] xl:w-auto xl:flex-1 xl:min-w-0">
              <div className="mb-4">
                <label className="control-label">TO DATE</label>
                <input
                  type="date"
                  className="form-control"
                  style={movementFilterInputStyle}
                  value={endDate}
                  onChange={handleEndDateChange}
                />
              </div>
        </Col>
      </Row>
      <Row className="mb-2 ">
        <Col className="w-full">
          <Card className="pt-6 mb-6">
            <CardBody>
            <div className="table-responsive">
              <table className="table table-centered table-nowrap">
                <thead className="thead-light">
                  <tr>
                    <th>Location</th>
                    <th>Stock In</th>
                    <th>Stock Out</th>
                    <th>Reason</th>
                    <th>Source</th>
                    <th>Message</th>
                    <th>Date</th>
                  </tr>
                </thead>
                
              
                  {
                    state === 'success' ? (
                      <tbody>
                       {sortedHistoryData.map((historyLine, index) => {
                        let inCount = []
                        let outCount = []
                        const movementLocationCode = historyLine?.location?.code
                        const movementLocationLabel = locationOptions?.find(
                          option => option.code === movementLocationCode
                        )?.label || historyLine?.location?.display_name || movementLocationCode
                        // A row recorded for reference only: its stock change is on another row.
                        const informational = isInformationalMovement(historyLine)
                        const recordedCount = informational
                          ? getInformationalMovementItems(historyLine.counts)
                          : []
                        if (!informational) {
                          historyLine.counts.forEach(count => {
                            getMovementSummaryDeltas(count).forEach(delta => {
                              const item = {
                                sku: count?.variant?.sku,
                                count: delta.quantity,
                                field: delta.field,
                                label: delta.label,
                              }
                              if (delta.quantity > 0) inCount.push(item)
                              if (delta.quantity < 0) outCount.push(item)
                            })
                          })
                        }
                        return (
                            <tr key={index}>
                              <td> {movementLocationLabel} </td>
                            <td>
                              <MovementSkuList
                                items={inCount}
                                type="in"
                                locationCode={movementLocationCode}
                                truncate
                              />
                            </td>
                            <td>
                              <MovementSkuList
                                items={outCount}
                                type="out"
                                locationCode={movementLocationCode}
                                truncate
                              />
                              {recordedCount.length > 0 && (
                                <div>
                                  <MovementSkuList
                                    items={recordedCount}
                                    type="record"
                                    locationCode={movementLocationCode}
                                    truncate
                                  />
                                </div>
                              )}
                            </td>
                            <td> {movementTypeToReason[historyLine?.movementType] || 'adjustment'} </td>
                            <td> {historyLine?.source ? stockMovementSourceLabel[historyLine.source] || historyLine.source : ''} </td>
                            <td> {historyLine.message} </td>
                              <td> {moment.tz(historyLine.createdAt, "Australia/Melbourne").format('D MMM, h:mm a z')}  </td>
                            </tr>
                        )
                      })} 
                      </tbody>
                      
                    ) : (
                      <TableSkeleton perPage={perPage} columns={7} />
                    )
                  }
                
              </table>
            </div>
            {
              (orders && state === 'success') && (
                <TablePagination
                  count={count}
                  page={page}
                  setPage={setPage}
                  perPage={perPage}
                  setPerPage={(nextPerPage) => {
                    setPerPage(nextPerPage)
                    setPage(1)
                    setPageCursors({ 1: undefined })
                  }}
                />
              ) 
            }
          </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  </div>
}

export default MovementPage
